import React, { FC, useCallback, useState } from 'react';
import classNames from 'classnames';
import { FieldInputProps, FieldMetaState } from 'react-final-form';
import getClassName from 'src/common/utils/getClassName';
import Modal from 'src/common/components/Modal';
import InstabookEditorHeader from 'src/calendar/components/InstabookEditorHeader';
import InstabookHeaderInfo from 'src/calendar/components/InstabookHeaderInfo';
import { INSTABOOK_EDITOR_DATES_FIELD, INSTABOOK_TRIP_DATE_MODAL_NAME as modalName } from 'src/calendar/constants';
import { todayDate } from 'src/common/utils/dateHelpers';
import Svg from 'src/common/components/Svg';
import { MAX_NEXT_MONTHS_INSTABOOK_OWNER, MAX_PREVIOUS_MONTHS } from 'src/calendar/hooks';

import useMediaQuery, { sharedMediaQueries } from 'src/common/hooks/useMediaQuery';
import { addMonths, subMonths } from 'date-fns';
import Button from 'src/common/components/Button';
import { DayPicker, type SelectMultipleEventHandler } from 'react-day-picker';
import 'src/common/components/DayPicker/react-day-picker.scss';
import DayContentWithMotivators from 'src/common/components/DayPicker/DayContentWithMotivators';
import s from './InstabookEditorDayPicker.module.scss';

const InstabookEditorDayPickerModal: FC<Props> = ({
  disabled,
  input: { onBlur, onChange, onFocus, value },
  meta: { error, touched },
  placeholder,
  withIcon = true,
}) => {
  const [selectedDays, setSelectedDays] = useState<Date[] | []>(value || []);

  const fieldName = INSTABOOK_EDITOR_DATES_FIELD;

  const datePickerLabel = 'Select Dates';

  const isMediumOrLarger = useMediaQuery(sharedMediaQueries.m);

  const handleOnSelect: SelectMultipleEventHandler = (days?: Date[]) => {
    if (Array.isArray(days)) {
      setSelectedDays(days);
    }
  };
  const isSelectedDaysEmpty = selectedDays === undefined || selectedDays.length < 1;

  const hasPastDates = selectedDays.some(d => d < todayDate());
  const hasFutureDates = selectedDays.some(d => d > todayDate());

  const futureDate = addMonths(new Date(), MAX_NEXT_MONTHS_INSTABOOK_OWNER);
  const pastYears = subMonths(new Date(), hasPastDates ? MAX_PREVIOUS_MONTHS : 0);

  const defaultMonth = selectedDays[0] ?? todayDate();

  const back = useCallback((prevDates?: Date[]) => {
    if (prevDates) {
      onChange(prevDates); // #8868
      setSelectedDays(prevDates);
    }
    onBlur();
  }, [onBlur, onChange, setSelectedDays]);

  const save = useCallback(() => {
    if (selectedDays) {
      onChange(selectedDays);
    }
    onBlur();
  }, [onBlur, onChange, selectedDays]);

  const handleClearPastDates = useCallback(() => setSelectedDays(
    selectedDays.filter(d => d >= todayDate()),
  ), [selectedDays, setSelectedDays]);

  const handleClearAllSelected = () => setSelectedDays([]);

  return (
    <div className={getClassName(s, 'root', classNames({ withValue: value }))}>
      <div className={getClassName(s, 'inputWrapper', classNames({ disabled }))}>
        {withIcon && (
          <label
            htmlFor={fieldName}
            className={s.label}
          >
            <Svg
              className={s.icon}
              href="#icon-calendar"
            />
          </label>
        )}
        <input
          disabled={disabled}
          className={s.input}
          id={fieldName}
          name={fieldName}
          readOnly
          onFocus={onFocus}
          placeholder={placeholder}
        />
      </div>

      {touched && error && (
        <label
          htmlFor={fieldName}
          className={s.error}
        >
          {error}
        </label>
      )}
      <Modal
        disableOverlayClick
        label={datePickerLabel}
        name={modalName}
        classNameModifier={classNames('calendar', 'instabook', 'tripDate')}
        closeModal={onBlur}
        shouldReturnFocusAfterClose={false}
        noExit
      >
        <InstabookEditorHeader
          icon={<Svg className={s.icon} href="#icon-calendar" />}
          title={datePickerLabel}
          classNameModifier="tripDate"
        >
          <InstabookHeaderInfo isDatePickerEditorOpen selectedDays={selectedDays} />
        </InstabookEditorHeader>
        <div className={s.dayPicker}>
          <div className={s.root}>
            <div className={s.actionButtons}>
              {hasPastDates && (
                <button
                  type="button"
                  className={s.clearPastAction}
                  onClick={handleClearPastDates}
                >
                  <Svg className={s.trashIcon} href="#trash-icon-with-arrow" />
                  <span>Clear past dates</span>
                </button>
              )}
              <button
                type="button"
                className={getClassName(s, 'clearSelectedAction', classNames({ isDisabled: isSelectedDaysEmpty }))}
                onClick={handleClearAllSelected}
                disabled={isSelectedDaysEmpty}
              >
                <Svg className={s.trashIcon} href="#trash-icon" />
                <span>Clear all selected</span>
              </button>
            </div>
            <div className={s.wrapper}>
              <DayPicker
                mode="multiple"
                defaultMonth={defaultMonth}
                numberOfMonths={isMediumOrLarger ? 2 : 1}
                selected={selectedDays}
                onSelect={handleOnSelect}
                fromDate={pastYears}
                toDate={futureDate}
                components={{ DayContent: DayContentWithMotivators }}
              />
            </div>
          </div>
          <div className={s.buttonsWrapper}>
            {!hasFutureDates && (
              <p className={s.warning}>Please select at least one future date to proceed.</p>
            )}
            <div className={s.buttons}>
              <Button
                type="button"
                classNameModifier="secondary paired"
                onClick={() => back(value)}
                fullWidth
              >
                <Svg
                  className={s.iconBack}
                  href="#icon-back"
                />
                Back
              </Button>
              <Button
                type="button"
                classNameModifier="paired"
                disabled={isSelectedDaysEmpty || !hasFutureDates}
                onClick={() => save()}
              >
                Continue
              </Button>
            </div>
          </div>
        </div>
      </Modal>

    </div>
  );
};

type Props = {
  disabled?: boolean;
  input: FieldInputProps<Date[], HTMLInputElement>;
  meta: FieldMetaState<Date[]>;
  placeholder?: string;
  withIcon?: boolean;
};

export default InstabookEditorDayPickerModal;
